// src/components/Manager/FinalizeMeetingPopup.jsx
import React, { useState, useEffect } from 'react';
import {
  X, FileText, CheckSquare, AlertCircle, Save, ArrowRight,
  Check, User, Calendar, Flag, Trash2, Plus, Mail,
  Sparkles, Clock, Loader2, ChevronRight, Edit3
} from 'lucide-react';

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`;
const getToken = () => localStorage.getItem('access_token');

// Fonction pour nettoyer le texte et supprimer les emojis
const sanitizeText = (text) => {
  if (!text) return '';
  return text
    .replace(/[\u{10000}-\u{10FFFF}]/gu, '')
    .replace(/[\u{1F000}-\u{1F6FF}]/gu, '')
    .replace(/[\u{2600}-\u{26FF}]/gu, '')
    .replace(/[📌📋✅⏰🔴🟠🟡🟢⚡💡🎯📅👥🔄📝⚠️❌✓►▪️•★☆✨]/g, '')
    .replace(/[^\p{L}\p{N}\s.,;:!?()\-'"]/gu, '')
    .trim();
};

// Fonction pour trouver dynamiquement l'ID du responsable
const findResponsableId = (responsableName, availableUsers) => {
  if (!responsableName || !availableUsers || availableUsers.length === 0) return null;
  
  const nameLower = responsableName.toLowerCase().trim();
  
  let user = availableUsers.find(u => u.name?.toLowerCase() === nameLower);
  
  if (!user) {
    user = availableUsers.find(u => 
      u.name?.toLowerCase().includes(nameLower) ||
      nameLower.includes(u.name?.toLowerCase())
    );
  }
  
  if (!user) {
    user = availableUsers.find(u => 
      u.email?.toLowerCase().split('@')[0] === nameLower
    );
  }
  
  return user ? user.id : null;
};

// Fonction pour normaliser les tâches (toutes sélectionnées par défaut)
const normalizeTasks = (tasks, availableUsers = []) => {
  if (!tasks || !Array.isArray(tasks)) return [];

  return tasks.map((task, idx) => {
    let responsableId = null;
    let responsableName = task.responsableName || task.responsable || "";
    
    if (responsableName && availableUsers.length > 0) {
      responsableId = findResponsableId(responsableName, availableUsers);
    }

    return {
      id: task.id || Date.now() + idx,
      description: sanitizeText(task.description || ""),
      responsableId: responsableId,
      responsableName: responsableName,
      priorite: task.priorite || "moyenne",
      deadline: task.deadline || "",
      selected: true,  // ← TOUTES LES TÂCHES SONT SÉLECTIONNÉES PAR DÉFAUT
    };
  });
};

const FinalizeMeetingPopup = ({
  isOpen,
  onClose,
  meetingId,
  meetingTitle,
  initialCompteRendu,
  initialTasks,
  onSave
}) => {
  const [compteRendu, setCompteRendu] = useState('');
  const [taches, setTaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [hoveredTask, setHoveredTask] = useState(null);
  
  // États pour l'édition du compte rendu
  const [editCR, setEditCR] = useState(false);
  const [tempCompteRendu, setTempCompteRendu] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (initialCompteRendu) {
        const cleanedText = sanitizeText(initialCompteRendu);
        setCompteRendu(cleanedText);
        setTempCompteRendu(cleanedText);
      }
      fetchUsers();
    }
  }, [isOpen, initialCompteRendu]);

  // Normaliser les tâches quand les utilisateurs sont chargés
  useEffect(() => {
    if (isOpen && initialTasks && Array.isArray(initialTasks) && initialTasks.length > 0 && availableUsers.length > 0) {
      const normalizedTasks = normalizeTasks(initialTasks, availableUsers);
      setTaches(normalizedTasks);
      console.log(`✅ ${normalizedTasks.length} tâches normalisées (toutes sélectionnées)`);
    } else if (isOpen && initialTasks && Array.isArray(initialTasks) && initialTasks.length > 0) {
      // Si pas encore d'utilisateurs, garder les tâches avec selected=true
      const tasksWithSelection = initialTasks.map(task => ({
        ...task,
        selected: true  // ← S'assurer que toutes les tâches sont sélectionnées
      }));
      setTaches(tasksWithSelection);
    }
  }, [isOpen, initialTasks, availableUsers]);

  const fetchUsers = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/users/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        let usersList = [];
        if (Array.isArray(data)) {
          usersList = data;
        } else if (data.results && Array.isArray(data.results)) {
          usersList = data.results;
        } else if (data.data && Array.isArray(data.data)) {
          usersList = data.data;
        }
        setAvailableUsers(usersList);
      }
    } catch (error) {
      console.error("Erreur chargement utilisateurs:", error);
    }
  };

  const handleCompteRenduChange = (e) => {
    setCompteRendu(e.target.value);
  };

  const handleTempCompteRenduChange = (e) => {
    setTempCompteRendu(e.target.value);
  };

  // Fonctions pour l'édition du compte rendu
  const startEditCR = () => {
    setTempCompteRendu(compteRendu);
    setEditCR(true);
  };

  const saveEditCR = () => {
    setCompteRendu(tempCompteRendu);
    setEditCR(false);
  };

  const cancelEditCR = () => {
    setEditCR(false);
    setTempCompteRendu(compteRendu);
  };

  const handleTaskToggle = (index) => {
    const newTaches = [...taches];
    newTaches[index].selected = !newTaches[index].selected;
    setTaches(newTaches);
  };

  const handleTaskChange = (index, field, value) => {
    const newTaches = [...taches];
    newTaches[index][field] = value;
    setTaches(newTaches);
  };

  const handleRemoveTask = (index) => {
    setTaches(taches.filter((_, i) => i !== index));
  };

  const handleAddTask = () => {
    setTaches([...taches, {
      id: Date.now(),
      description: '',
      responsableId: '',
      responsableName: '',
      priorite: 'moyenne',
      deadline: '',
      selected: true  // ← Nouvelle tâche aussi sélectionnée par défaut
    }]);
  };

  // Sélectionner/Désélectionner toutes les tâches
  const toggleSelectAll = () => {
    const allSelected = taches.every(t => t.selected);
    setTaches(taches.map(t => ({ ...t, selected: !allSelected })));
  };

  // Compter les tâches sélectionnées
  const selectedCount = taches.filter(t => t.selected && t.description?.trim()).length;
  const totalCount = taches.filter(t => t.description?.trim()).length;

  const validateStep1 = () => {
    if (!compteRendu || !compteRendu.trim()) {
      setError("Le compte rendu ne peut pas être vide");
      return false;
    }
    setError('');
    return true;
  };

  const validateStep2 = () => {
    const selectedTasks = taches.filter(t => t.selected && t.description && t.description.trim());
    if (selectedTasks.length === 0) {
      setError("Sélectionnez au moins une tâche");
      return false;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    setError('');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const token = getToken();
      if (!token) {
        setError("Vous devez être connecté");
        setLoading(false);
        return;
      }

      const reunionId = parseInt(meetingId);
      if (isNaN(reunionId) || reunionId <= 0) {
        setError("ID de réunion invalide");
        setLoading(false);
        return;
      }

      const cleanCompteRendu = sanitizeText(compteRendu);

      if (!cleanCompteRendu.trim()) {
        setError("Le compte rendu ne peut pas être vide");
        setLoading(false);
        return;
      }

      // 1. Sauvegarder le compte rendu
      let crResponse = await fetch(`${API_URL}/comptes-rendus/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reunion: reunionId,
          resume: cleanCompteRendu,
          decisions: [],
          points_cles: [],
          statut: 'publie'
        })
      });

      if (crResponse.status === 500) {
        const checkResponse = await fetch(`${API_URL}/comptes-rendus/?reunion=${reunionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await checkResponse.json();
        const comptesList = data.results || data;
        const existingCR = comptesList.find(cr => cr.reunion === reunionId);

        if (existingCR) {
          crResponse = await fetch(`${API_URL}/comptes-rendus/${existingCR.id}/`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              reunion: reunionId,
              resume: cleanCompteRendu,
              decisions: [],
              points_cles: [],
              statut: 'publie'
            })
          });
        }
      }

      if (!crResponse.ok) {
        throw new Error("Erreur lors de la sauvegarde du compte rendu");
      }

      // 2. Supprimer les anciennes tâches
      const existingTasksResponse = await fetch(`${API_URL}/taches/?reunion=${reunionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (existingTasksResponse.ok) {
        const existingData = await existingTasksResponse.json();
        const existingTasksList = existingData.results || existingData;
        for (const task of existingTasksList) {
          await fetch(`${API_URL}/taches/${task.id}/`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        }
      }

      // 3. Sauvegarder uniquement les tâches sélectionnées
      const selectedTasks = taches.filter(t => t.selected && t.description && t.description.trim());
      let tasksSaved = 0;

      for (const task of selectedTasks) {
        try {
          let responsableId = null;
          if (task.responsableName) {
            responsableId = findResponsableId(task.responsableName, availableUsers);
          }

          const cleanDescription = sanitizeText(task.description);

          let formattedDeadline = null;
          if (task.deadline) {
            try {
              const deadlineDate = new Date(task.deadline);
              if (!isNaN(deadlineDate.getTime())) {
                formattedDeadline = deadlineDate.toISOString();
              }
            } catch (e) { }
          }

          const taskData = {
            reunion: reunionId,
            description: cleanDescription,
            responsable: responsableId,
            priorite: task.priorite || "moyenne",
            statut: "a_faire",
            deadline: formattedDeadline
          };

          const taskResponse = await fetch(`${API_URL}/taches/`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(taskData)
          });

          if (taskResponse.ok) {
            tasksSaved++;
          }
        } catch (taskError) {
          console.error("Erreur tâche:", taskError);
        }
      }

      if (tasksSaved > 0) {
        setSuccess(true);
        if (onSave) onSave({ compte_rendu: cleanCompteRendu, taches: selectedTasks });
        setTimeout(() => onClose(), 2000);
      } else {
        setError("Aucune tâche n'a pu être sauvegardée");
      }

    } catch (err) {
      console.error("Erreur:", err);
      setError(err.message || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const getPriorityLabel = (priorite) => {
    const labels = {
      critique: { text: 'Critique', color: '#ef4444', bg: '#fee2e2', icon: '!' },
      haute: { text: 'Haute', color: '#f97316', bg: '#ffedd5', icon: '!' },
      moyenne: { text: 'Moyenne', color: '#eab308', bg: '#fef3c7', icon: '!' },
      basse: { text: 'Basse', color: '#16a34a', bg: '#dcfce7', icon: '!' }
    };
    return labels[priorite] || labels.moyenne;
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <div style={styles.overlay}>
        <div style={styles.successModal}>
          <div style={styles.successIcon}><Check size={48} /></div>
          <h2 style={styles.successTitle}>Réunion finalisée !</h2>
          <p style={styles.successText}>Le compte rendu a été sauvegardé et les tâches ont été assignées.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.headerLeft}><Sparkles size={22} color="#5e17eb" /><h2 style={styles.title}>Finaliser la réunion</h2></div>
          <button onClick={onClose} style={styles.closeButton}><X size={20} /></button>
        </div>

        <div style={styles.progressContainer}>
          <div style={styles.progressStep}><div style={{ ...styles.progressCircle, background: step >= 1 ? '#5e17eb' : '#e5e7eb', color: step >= 1 ? 'white' : '#9ca3af' }}>{step > 1 ? <Check size={14} /> : 1}</div><span style={{ ...styles.progressLabel, color: step >= 1 ? '#5e17eb' : '#9ca3af' }}>Compte rendu</span></div>
          <div style={{ ...styles.progressLine, background: step >= 2 ? '#5e17eb' : '#e5e7eb' }} />
          <div style={styles.progressStep}><div style={{ ...styles.progressCircle, background: step >= 2 ? '#5e17eb' : '#e5e7eb', color: step >= 2 ? 'white' : '#9ca3af' }}>{step > 2 ? <Check size={14} /> : 2}</div><span style={{ ...styles.progressLabel, color: step >= 2 ? '#5e17eb' : '#9ca3af' }}>Tâches</span></div>
          <div style={{ ...styles.progressLine, background: step >= 3 ? '#5e17eb' : '#e5e7eb' }} />
          <div style={styles.progressStep}><div style={{ ...styles.progressCircle, background: step >= 3 ? '#5e17eb' : '#e5e7eb', color: step >= 3 ? 'white' : '#9ca3af' }}>3</div><span style={{ ...styles.progressLabel, color: step >= 3 ? '#5e17eb' : '#9ca3af' }}>Confirmation</span></div>
        </div>

        {error && <div style={styles.error}><AlertCircle size={16} /><span>{error}</span></div>}

        {step === 1 && (
          <div style={styles.stepContent}>
            <div style={styles.crCard}>
              <div style={styles.crCardHeader}>
                <FileText size={18} color="#5e17eb" />
                <h3 style={styles.crCardTitle}>Compte rendu de la réunion</h3>
                {!editCR ? (
                  <button style={styles.editButton} onClick={startEditCR}>
                    <Edit3 size={14} /> Modifier
                  </button>
                ) : (
                  <div style={styles.editActions}>
                    <button style={styles.editSaveButton} onClick={saveEditCR}>
                      <Check size={14} /> OK
                    </button>
                    <button style={styles.editCancelButton} onClick={cancelEditCR}>
                      <X size={14} /> Annuler
                    </button>
                  </div>
                )}
              </div>
              {editCR ? (
                <textarea 
                  style={styles.textareaEdit} 
                  value={tempCompteRendu} 
                  onChange={handleTempCompteRenduChange}
                  placeholder="Rédigez le compte rendu détaillé de la réunion..."
                  rows={12}
                />
              ) : (
                <textarea 
                  style={styles.textarea} 
                  value={compteRendu} 
                  onChange={handleCompteRenduChange} 
                  placeholder="Rédigez le compte rendu détaillé de la réunion..." 
                  rows={12} 
                />
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div style={styles.stepContent}>
            <div style={styles.tasksHeader}>
              <div style={styles.tasksHeaderLeft}>
                <CheckSquare size={18} color="#5e17eb" />
                <h3 style={styles.tasksTitle}>Tâches à assigner</h3>
              </div>
              <div style={styles.tasksHeaderRight}>
                <button onClick={toggleSelectAll} style={styles.selectAllButton}>
                  {selectedCount === totalCount && totalCount > 0 ? "Tout désélectionner" : "Tout sélectionner"}
                </button>
                <span style={styles.tasksBadge}>{selectedCount}/{totalCount} sélectionnée(s)</span>
              </div>
            </div>

            <div style={styles.tasksList}>
              {taches.length === 0 ? (
                <div style={styles.emptyTasks}><CheckSquare size={48} color="#d1d5db" /><p>Aucune tâche détectée</p><button style={styles.addTaskBtnEmpty} onClick={handleAddTask}><Plus size={16} /> Ajouter une tâche</button></div>
              ) : (
                taches.map((task, index) => {
                  const priority = getPriorityLabel(task.priorite);
                  return (
                    <div key={task.id || index} style={styles.taskCard}>
                      <div style={styles.taskCardContent}>
                        <div style={styles.taskMainRow}>
                          <input 
                            type="checkbox" 
                            checked={task.selected} 
                            onChange={() => handleTaskToggle(index)} 
                            style={styles.checkbox} 
                          />
                          <div style={styles.taskNumber}>{index + 1}</div>
                          <div style={styles.taskInfo}>
                            <div style={styles.taskDescription}>{task.description || "Description de la tâche"}</div>
                            <div style={styles.taskMeta}>
                              <div style={styles.metaItem}>
                                <User size={14} color="#6b7280" />
                                <select
                                  value={task.responsableName || ""}
                                  onChange={(e) => handleTaskChange(index, 'responsableName', e.target.value)}
                                  style={styles.selectSmall}
                                >
                                  <option value="">Non assigné</option>
                                  {availableUsers.map(user => (
                                    <option key={user.id} value={user.name}>
                                      {user.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div style={styles.metaItem}>
                                <Flag size={14} color="#6b7280" />
                                <select
                                  value={task.priorite}
                                  onChange={(e) => handleTaskChange(index, 'priorite', e.target.value)}
                                  style={styles.selectSmall}
                                >
                                  <option value="basse">Basse</option>
                                  <option value="moyenne">Moyenne</option>
                                  <option value="haute">Haute</option>
                                  <option value="critique">Critique</option>
                                </select>
                              </div>
                              <div style={styles.metaItem}>
                                <Calendar size={14} color="#6b7280" />
                                <input
                                  type="date"
                                  value={task.deadline || ""}
                                  onChange={(e) => handleTaskChange(index, 'deadline', e.target.value)}
                                  style={styles.dateInput}
                                />
                              </div>
                            </div>
                          </div>
                          <button onClick={() => handleRemoveTask(index)} style={styles.removeButton}><Trash2 size={16} /></button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <button style={styles.addTaskButton} onClick={handleAddTask}><Plus size={16} /> Ajouter une tâche</button>
            <p style={styles.hintText}>💡 Décochez les tâches que vous ne voulez pas sauvegarder</p>
          </div>
        )}

        {step === 3 && (
          <div style={styles.stepContent}>
            <div style={styles.confirmCard}>
              <div style={styles.confirmHeader}><FileText size={18} color="#5e17eb" /><h3 style={styles.confirmTitle}>Récapitulatif</h3></div>
              <div style={styles.confirmSection}><h4 style={styles.confirmSubtitle}>Compte rendu</h4><div style={styles.confirmCrPreview}><p>{compteRendu.length > 200 ? compteRendu.substring(0, 200) + '...' : compteRendu}</p></div></div>
              <div style={styles.confirmSection}><h4 style={styles.confirmSubtitle}>Tâches ({selectedCount})</h4><div style={styles.confirmTasksList}>{taches.filter(t => t.selected && t.description?.trim()).map((task, i) => (<div key={i} style={styles.confirmTaskItem}><div style={styles.confirmTaskNumber}>{i + 1}</div><div style={styles.confirmTaskInfo}><div style={styles.confirmTaskDesc}>{task.description}</div><div style={styles.confirmTaskDetails}><span style={styles.confirmTaskResp}><User size={12} /> {task.responsableName || "Non assigné"}</span><span style={{ ...styles.confirmTaskPriority, backgroundColor: getPriorityLabel(task.priorite).bg, color: getPriorityLabel(task.priorite).color }}>{getPriorityLabel(task.priorite).icon} {getPriorityLabel(task.priorite).text}</span>{task.deadline && <span style={styles.confirmTaskDeadline}><Calendar size={12} /> {new Date(task.deadline).toLocaleDateString('fr-FR')}</span>}</div></div></div>))}</div></div>
              <div style={styles.warningBox}><AlertCircle size={16} /><span>Une fois confirmé, la réunion sera marquée comme terminée</span></div>
            </div>
          </div>
        )}

        <div style={styles.footer}>
          <button style={styles.cancelButton} onClick={onClose} disabled={loading}>Annuler</button>
          <div style={styles.footerRight}>
            {step > 1 && <button style={styles.backButton} onClick={handleBack} disabled={loading}>Retour</button>}
            {step < 3 ? <button style={styles.nextButton} onClick={handleNext} disabled={loading}>Suivant <ChevronRight size={16} /></button> : <button style={styles.submitButton} onClick={handleSubmit} disabled={loading}>{loading ? <Loader2 size={16} className="spin" /> : <Save size={16} />}<span>{loading ? 'Finalisation...' : 'Finaliser'}</span></button>}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: 24, width: '90%', maxWidth: 800, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  successModal: { background: 'white', borderRadius: 24, width: 400, padding: 40, textAlign: 'center' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #f0f0f0' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  title: { fontSize: 20, fontWeight: 600, color: '#1a1a2e', margin: 0 },
  closeButton: { width: 32, height: 32, borderRadius: 16, background: '#f5f5f5', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' },
  progressContainer: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 24px', background: '#faf9ff', borderBottom: '1px solid #f0f0f0' },
  progressStep: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
  progressCircle: { width: 32, height: 32, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600 },
  progressLine: { width: 60, height: 2, margin: '0 10px' },
  progressLabel: { fontSize: 11, fontWeight: 500 },
  error: { margin: '16px 24px 0', padding: 12, background: '#fee2e2', borderRadius: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 },
  stepContent: { flex: 1, overflowY: 'auto', padding: '20px 24px' },
  crCard: { background: '#faf9ff', borderRadius: 16, border: '1px solid #ede9ff', overflow: 'hidden' },
  crCardHeader: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: 'white', borderBottom: '1px solid #f0f0f0' },
  crCardTitle: { fontSize: 15, fontWeight: 600, color: '#333', margin: 0, flex: 1 },
  editButton: {
    padding: '4px 12px',
    borderRadius: 20,
    border: '1px solid #5e17eb',
    background: 'transparent',
    color: '#5e17eb',
    cursor: 'pointer',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    transition: 'all 0.2s ease',
    ':hover': {
      background: '#ede9ff',
      transform: 'scale(1.02)',
    }
  },
  editActions: {
    display: 'flex',
    gap: 6,
  },
  editSaveButton: {
    padding: '4px 12px',
    borderRadius: 20,
    border: 'none',
    background: '#22c55e',
    color: 'white',
    cursor: 'pointer',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    transition: 'all 0.2s ease',
    ':hover': {
      background: '#16a34a',
      transform: 'scale(1.02)',
    }
  },
  editCancelButton: {
    padding: '4px 12px',
    borderRadius: 20,
    border: '1px solid #f44336',
    background: 'transparent',
    color: '#f44336',
    cursor: 'pointer',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    transition: 'all 0.2s ease',
    ':hover': {
      background: '#fee2e2',
      transform: 'scale(1.02)',
    }
  },
  textarea: { width: '100%', padding: 16, border: 'none', fontSize: 14, lineHeight: 1.6, fontFamily: 'inherit', resize: 'vertical', background: '#faf9ff', outline: 'none' },
  textareaEdit: { width: '100%', padding: 16, border: 'none', fontSize: 14, lineHeight: 1.6, fontFamily: 'inherit', resize: 'vertical', background: '#fff', outline: 'none', border: '2px solid #5e17eb', borderRadius: '8px' },
  tasksHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  tasksHeaderLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  tasksHeaderRight: { display: 'flex', alignItems: 'center', gap: 12 },
  tasksTitle: { fontSize: 16, fontWeight: 600, color: '#333', margin: 0 },
  tasksBadge: { fontSize: 12, background: '#ede9ff', color: '#5e17eb', padding: '4px 12px', borderRadius: 20, fontWeight: 500 },
  selectAllButton: { fontSize: 12, background: 'none', border: 'none', color: '#5e17eb', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, ':hover': { background: '#ede9ff' } },
  tasksList: { display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 380, overflowY: 'auto', marginBottom: 16 },
  emptyTasks: { textAlign: 'center', padding: 40, background: '#faf9ff', borderRadius: 16, color: '#9ca3af' },
  addTaskBtnEmpty: { marginTop: 16, padding: '8px 20px', background: '#5e17eb', color: 'white', border: 'none', borderRadius: 30, cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 },
  taskCard: { background: '#faf9ff', borderRadius: 14, border: '1px solid #f0f0f0', padding: 14, marginBottom: 10 },
  taskCardContent: { display: 'flex', flexDirection: 'column' },
  taskMainRow: { display: 'flex', gap: 12, alignItems: 'flex-start' },
  checkbox: { width: 18, height: 18, marginTop: 2, cursor: 'pointer', accentColor: '#5e17eb' },
  taskNumber: { width: 26, height: 26, borderRadius: 13, background: '#ede9ff', color: '#5e17eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, flexShrink: 0 },
  taskInfo: { flex: 1 },
  taskDescription: { fontSize: 14, fontWeight: 500, color: '#1a1a2e', marginBottom: 10, lineHeight: 1.4 },
  taskMeta: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  metaItem: { display: 'flex', alignItems: 'center', gap: 6, background: 'white', padding: '6px 12px', borderRadius: 20, border: '1px solid #e5e7eb', fontSize: 12 },
  selectSmall: { border: 'none', background: 'transparent', fontSize: 12, outline: 'none', cursor: 'pointer', padding: '4px' },
  dateInput: { border: 'none', background: 'transparent', fontSize: 12, outline: 'none', cursor: 'pointer', padding: '4px' },
  removeButton: { background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  addTaskButton: { width: '100%', padding: 12, background: 'none', border: '1px dashed #5e17eb', borderRadius: 12, color: '#5e17eb', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 8 },
  hintText: { fontSize: 11, color: '#9ca3af', marginTop: 8, textAlign: 'center', fontStyle: 'italic' },
  confirmCard: { background: '#faf9ff', borderRadius: 16, border: '1px solid #ede9ff', overflow: 'hidden' },
  confirmHeader: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: 'white', borderBottom: '1px solid #f0f0f0' },
  confirmTitle: { fontSize: 16, fontWeight: 600, color: '#333', margin: 0 },
  confirmSection: { padding: '16px', borderBottom: '1px solid #f0f0f0' },
  confirmSubtitle: { fontSize: 13, fontWeight: 600, color: '#5e17eb', marginBottom: 12 },
  confirmCrPreview: { background: 'white', padding: 12, borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 13, color: '#4b5563', lineHeight: 1.5 },
  confirmTasksList: { display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 250, overflowY: 'auto' },
  confirmTaskItem: { display: 'flex', gap: 12, background: 'white', padding: 12, borderRadius: 12, border: '1px solid #e5e7eb' },
  confirmTaskNumber: { width: 24, height: 24, borderRadius: 12, background: '#ede9ff', color: '#5e17eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 },
  confirmTaskInfo: { flex: 1 },
  confirmTaskDesc: { fontSize: 13, fontWeight: 500, color: '#1a1a2e', marginBottom: 6 },
  confirmTaskDetails: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: 11 },
  confirmTaskResp: { display: 'flex', alignItems: 'center', gap: 4, color: '#5e17eb' },
  confirmTaskPriority: { display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 500 },
  confirmTaskDeadline: { display: 'flex', alignItems: 'center', gap: 4, color: '#6b7280' },
  warningBox: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: '#fef3c7', fontSize: 12, color: '#d97706' },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderTop: '1px solid #f0f0f0', background: 'white' },
  footerRight: { display: 'flex', gap: 12 },
  cancelButton: { padding: '8px 20px', background: '#f5f5f5', border: '1px solid #e5e7eb', borderRadius: 40, cursor: 'pointer', fontSize: 13, color: '#666' },
  backButton: { padding: '8px 20px', background: '#f5f5f5', border: '1px solid #e5e7eb', borderRadius: 40, cursor: 'pointer', fontSize: 13, color: '#666' },
  nextButton: { padding: '8px 24px', background: '#5e17eb', color: 'white', border: 'none', borderRadius: 40, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 },
  submitButton: { padding: '8px 24px', background: '#22c55e', color: 'white', border: 'none', borderRadius: 40, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 },
  successIcon: { width: 80, height: 80, borderRadius: 40, background: '#22c55e', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' },
  successTitle: { fontSize: 24, fontWeight: 600, color: '#1a1a2e', marginBottom: 8 },
  successText: { color: '#6b7280' },
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }.spin { animation: spin 1s linear infinite; }`;
document.head.appendChild(styleSheet);

export default FinalizeMeetingPopup;